/**
 * m3 echo Frontend - Support Button Component
 *
 * Button to access support functionality.
 * - Regular users: Opens support form dialog
 * - Admins: Opens support inbox with unread badge
 */

import React, { useState, useEffect } from 'react';
import { Badge, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import SupportIcon from '@mui/icons-material/Support';
import { useAuthStore } from '../stores/useAuthStore';
import { supportApi } from '../services/api';
import SupportFormDialog from './SupportFormDialog';
import SupportInboxDialog from './SupportInboxDialog';

export const SupportButton: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.is_admin || false;

  const [unreadCount, setUnreadCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch unread count for admins
  useEffect(() => {
    if (isAdmin) {
      fetchUnreadCount();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchUnreadCount = async () => {
    try {
      const response = await supportApi.getUnreadCount();
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    // Refresh unread count after closing inbox
    if (isAdmin) {
      fetchUnreadCount();
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          borderRadius: 1,
          border: '1px solid #e5e7eb',
          mb: 1,
          color: '#6b7280',
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: '#6b7280' }}>
          {isAdmin && unreadCount > 0 ? (
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: '16px',
                  minWidth: '16px',
                  padding: '0 4px',
                },
              }}
            >
              <SupportIcon />
            </Badge>
          ) : (
            <SupportIcon />
          )}
        </ListItemIcon>
        <ListItemText
          primary="Support"
          primaryTypographyProps={{
            fontSize: '0.95rem',
            fontWeight: 500,
          }}
        />
      </ListItemButton>

      {/* Render appropriate dialog based on user role */}
      {isAdmin ? (
        <SupportInboxDialog
          open={dialogOpen}
          onClose={handleClose}
        />
      ) : (
        <SupportFormDialog
          open={dialogOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default SupportButton;
